<template>
  <BasePaneCard
    headingTooltip="Entenda os principais motivos de<br>conversão dos atendimentos."
    :isLoading="isLoading"
    :unboxed="unboxed"
  >
    <div
      class="conversion-reasons"
      :class="{ 'conversion-reasons--compact': compactMode }"
    >
      <div
        v-if="conversions.length"
        class="conversion-reasons__container"
      >
        <div class="conversion-reasons__top">
          <ul>
            <li v-for="(item, index) in conversionsTop" :key="index" class="conversion-reasons__top__item">
              <div class="conversion-reasons__top__item__bar">
                <BasePercentBar class="conversion-reasons__top__item__bar__progress"
                  :label="`${item.description} (${item.total})`" :dataset="item.percentage" />

                <div v-if="!compactMode" class="conversion-reasons__top__item__bar__button-lead-conversations">
                  <button v-tooltip="{
                    theme: 'tooltip-right',
                    content: 'Abrir lista de<br>conversas.'
                  }" @click.prevent="openLeadConversations(item.description)">
                    <SvgIconChat />
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="conversionsOthers.length && !compactMode" class="conversion-reasons__others">
          <h5 class="heading heading--smaller">
            Outros motivos
          </h5>

          <ul>
            <li v-for="(conversion, index) in conversionsOthers" :key="index">
              <button v-tooltip="{
                theme: 'tooltip-right',
                content: 'Abrir lista<br>de conversas.'
              }" @click.prevent="openLeadConversations(conversion.description)">
                {{ conversion.description }} ({{ conversion.total }})
              </button>
            </li>
          </ul>
        </div>
      </div>

      <BaseErrorState
        v-else-if="dataset?.error"
        icon="top"
        align="center"
        :fillBackground="true"
        class="conversion-reasons__empty-state conversion-reasons__empty-state--no-data"
      />

      <BaseEmptyState class="conversion-reasons__empty-state conversion-reasons__empty-state--no-data" v-else>
        <template v-if="dataset?.error">
          <SvgIconRobot />

          <h3>
            Ops! Algo deu errado...
          </h3>

          <p>
            <a href="javascript:;" @click.stop="fetchData()">
              Tentar novamente
            </a>
          </p>
        </template>

        <template v-else-if="isNotApplicableStatusFilter">
          <SvgIconListExclamation />

          <h3>
            As conversas filtradas por este status<br>
            não possuem motivo de conversão.
          </h3>
        </template>

        <template v-else>
          <SvgIconRunningWithErrors />

          <h3>
            Nenhum motivo para ser exibido
          </h3>

          <p>
            Confira os filtros selecionados<br>
            e atualize a página.
          </p>
        </template>
      </BaseEmptyState>

      <CommonLeadConversations :isOpen="isOpenLeadConversations" :heading="leadConversationsHeading"
        :urlParams="leadConversations.urlParams" />
    </div>

    <div class="conversion-reasons__link-container" v-if="!unboxed">
      <RouterLink class="conversion-reasons__link" to="insight-conversations">
        <span>
          Veja todos os motivos de conversão
          <SvgIconArrowRight style="height: 10px; transform: translate(-5px, -1px)" />
        </span>
      </RouterLink>
    </div>
  </BasePaneCard>
</template>

<script>
  import { dateFilter, percentageFilter } from '@/utils/filters'
  import { pageFilterExceptionMixin } from '@/utils/mixins'
  import BasePaneCard from '@/components/base/BasePaneCard'
  import BaseEmptyState from '@/components/base/BaseEmptyState'
  import BasePercentBar from '@/components/base/BasePercentBar'
  import CommonLeadConversations from '@/components/common/CommonLeadConversations'
  import { mapState, mapGetters } from 'vuex'
  import axios from 'axios'
  import dayjs from 'dayjs'
  import { orderBy } from 'lodash'

  export default {
    name: 'WidgetAttendancesConversion',

    components: {
      BasePaneCard,
      BaseEmptyState,
      BasePercentBar,
      CommonLeadConversations
    },

    props: {
      showIcon: Boolean,
      compactMode: Boolean,
      unboxed: Boolean
    },

    filters: {
      date: dateFilter,
      percentage: percentageFilter
    },

    mixins: [pageFilterExceptionMixin],

    data() {
      return {
        isLoading: false,
        dataset: null,
        isOpenLeadConversations: false,
        leadConversations: {
          filter: 'conversion',
          key: '',
          urlParams: ''
        }
      }
    },

    computed: {
      ...mapState('base', [
        'mainApi',
        'pageFilter'
      ]),

      ...mapGetters('auth', [
        'userId',
        'platformId'
      ]),

      ...mapGetters('smartSummary', [
        'getConversionReasonsPrompt',
      ]),

      conversions() {
        if (!this.dataset?.conversion || this.dataset.error) {
          return []
        }

        const {
          conversion_tabulations: conversions,
          total_conversion: totalConversions
        } = this.dataset.conversion

        const dataset = conversions.map(item => {
          let [description, total] = item

          return {
            description,
            total,
            percentage: (total / totalConversions) * 100
          }
        })

        return orderBy(dataset, ['percentage'], ['desc'])
      },

      conversionsTop() {
        return this.conversions.slice(0, 5)
      },

      conversionsOthers() {
        return this.conversions.slice(5)
      },

      leadConversationsHeading() {
        const { key } = this.leadConversations

        return key || ''
      },

      isNotApplicableStatusFilter() {
        return this.pageFilter?.status?.length && !this.pageFilter?.status?.includes(16)
      }
    },

    watch: {
      pageFilter() {
        this.dataset = null
        this.fetchData()
      }
    },

    mounted() {
      this.$root.$on('pane-tray-close', this.closeLeadConversations)
      this.fetchData()
    },

    methods: {
      getFetchDataParams (leadConversationsMode) {
        let params = '?'
        let [dateStart, dateEnd] = this.pageFilter.dateRange
        const { action, csat, feeling } = this.pageFilter

        if (!dateStart || !dateEnd) {
          dateStart = dayjs()
          dateEnd = dateStart.add(1, 'day')
        }

        if (leadConversationsMode) {
          params += `&filter=${this.leadConversations.filter}`
          params += `&key=${encodeURIComponent(this.leadConversations.key)}`
        }

        params += `&firstDate=${dateFilter(dateStart, 'base')}`
        params += `&lastDate=${dateFilter(dateEnd, 'base')}`

        if (action.length) {
          params += `&actions=${action.join()}`
        }

        if (csat.length) {
          params += `&csat=${csat.join()}`
        }

        if (feeling.length) {
          params += `&sentiments=${feeling.join()}`
        }

        return params
          .replace(/\?$/, '')
          .replace(/\?&/, '?')
      },

      async fetchData() {
        if (this.isNotApplicableStatusFilter) return
        this.isLoading = true
        try {
          const params = this.getFetchDataParams()
          // const url = `${this.mainApi}lead/attendances/3/reasons${params}`
          const url = `${this.mainApi}lead/conversion-reasons${params}`
          const response = await axios.get(url)
          this.dataset = response.data
        } catch (error) {
          this.dataset = { error }
          throw error
        } finally {
          this.isLoading = false
        }
      },

      updateLeadConversations(key) {
        this.leadConversations.key = key
        this.leadConversations.urlParams = this.getFetchDataParams(true)
      },

      openLeadConversations(key) {
        this.$root.$emit('pane-tray-close', 'leadConversationsContext')

        this.updateLeadConversations(key)

        this.isOpenLeadConversations = true
      },

      closeLeadConversations(context) {
        if (context === 'leadConversationsContext') {
          this.isOpenLeadConversations = false
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  $blue-dark: nth($colors-custom, 1);
  $blue-light: nth($colors-custom, 2);

  .conversion-reasons {
    height: 430px;
    padding-top: 15px;

    &--compact {
      height: auto;
    }

    &__container {
      display: flex;
      height: 100%;
      flex-direction: column;
    }

    &__top {
      &__item {
        &:not(:first-child) {
          margin-top: 15px;
        }

        &__bar {
          display: flex;
          align-items: flex-end;

          &__progress {
            flex-grow: 1;
          }

          &__button-lead-conversations {
            flex: 0 1 30px;
            display: flex;
            justify-content: flex-end;

            button {
              width: 16px;
              height: 16px;
              padding: 0;
              border: 0;
              transform: translateY(-4px);
              background: none;

              &:hover {
                svg {
                  fill: nth($colors-custom, 2);
                }
              }

              svg {
                transition: all 0.1s;
                fill: nth($colors-custom, 1);
              }
            }
          }
        }
      }
    }

    &__others {
      @extend %scrollbar;

      flex-grow: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid nth($colors-custom, 4);

      ul {
        display: flex;
        overflow: auto;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 8px;
        padding-right: 15px;
        margin-top: 15px;

        li {
          display: inline-block;

          button {
            padding: 8px 10px;
            border: 1px solid nth($colors-custom, 1);
            border-radius: 2em;
            transition: all 0.1s;
            background: none;

            font: {
              weight: 400;
              size: 12px;
            }

            line-height: 100%;

            &:hover {
              border-color: nth($colors-custom, 10);
              background: nth($colors-custom, 10);
            }
          }
        }
      }
    }

    &__link-container {
      margin-top: 20px;
      text-align: center;
    }

    &__link {
      color: $blue-dark;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      letter-spacing: 0.5px;

      &:hover {
        color: $blue-light;

        path {
          fill: $blue-light;
        }
      }
    }

    &__empty-state {
      height: 228px;
      padding: 20px;
      color: $blue-dark;
      background: rgba($blue-light, 0.08);
      width: 100%;
      border-radius: 15px;

      &--disabled {
        svg {
          fill: $blue-dark;
        }
      }

      &--no-data {
        svg {
          // stroke: $blue-dark;
          fill: $blue-dark;
          stroke-width: 2px;
          height: 30px;
        }
      }
    }
  }
</style>
